'use client';

import type { Provider } from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { ResourceTypes } from './resourceTypes';

const wipResourceTypes = [
  ResourceType.ResourceTypeUnspecified,
  ResourceType.AwsEfs,
  ResourceType.AwsFsx,
  ResourceType.AwsKeyspacesTable,
  ResourceType.AwsRedshift,
  ResourceType.AwsDocumentdb,
  ResourceType.AwsNeptune,
  ResourceType.AzureFileShare,
  ResourceType.AzureDisk,
  ResourceType.AzureSapHanaVm,
  ResourceType.GcpSapHanaVm,
  ResourceType.AzureSqlDatabase,
  ResourceType.AzureSqlManagedInstance,
  ResourceType.AzureMysql,
  ResourceType.AzurePostgresql,
  ResourceType.AzureCosmosdbMongodb,
  ResourceType.AzureCosmosdbNosql,
  ResourceType.AzureAksNamespace,
  ResourceType.GcpGkeNamespace,
  ResourceType.GcpCloudFirestore,
  ResourceType.GoogleWorkspaceResource,
];

export const useSupportedResourceTypes = (
  cloudProviders: Provider[] = []
): ResourceType[] => {
  const { isDemo, isDev } = useEnvironment();
  const {
    AzureSqlDatabaseSupport,
    SapHanaPitrSupport,
    AwsRedshiftDiscoverySupport,
    AwsKeyspacesSupport,
    AwsDocumentDbSupport,
    NeptuneBackupSupport,
    AzureFilesSupport,
  } = useFeatureFlags();

  return useMemo(() => {
    const ffResources = [
      ResourceType.AwsFsx,
      ResourceType.AwsEfs,
      ...(AwsKeyspacesSupport ? [ResourceType.AwsKeyspacesTable] : []),
      ...(AzureSqlDatabaseSupport ? [ResourceType.AzureSqlDatabase] : []),
      ...(SapHanaPitrSupport
        ? [ResourceType.AzureSapHanaVm, ResourceType.GcpSapHanaVm]
        : []),
      ...(AwsRedshiftDiscoverySupport ? [ResourceType.AwsRedshift] : []),
      ...(AwsDocumentDbSupport ? [ResourceType.AwsDocumentdb] : []),
      ...(NeptuneBackupSupport ? [ResourceType.AwsNeptune] : []),
      ...(AzureFilesSupport ? [ResourceType.AzureFileShare] : []),
    ];

    return Object.values(ResourceType).filter(
      (x) =>
        x !== ResourceType.ResourceTypeUnspecified &&
        (cloudProviders.length
          ? cloudProviders.includes(ResourceTypes[x]?.provider)
          : true) &&
        (isDemo ||
          isDev ||
          !wipResourceTypes.includes(x) ||
          ffResources.includes(x))
    );
  }, [
    AwsKeyspacesSupport,
    AzureSqlDatabaseSupport,
    SapHanaPitrSupport,
    AwsRedshiftDiscoverySupport,
    AwsDocumentDbSupport,
    NeptuneBackupSupport,
    AzureFilesSupport,
    isDemo,
    isDev,
    cloudProviders,
  ]);
};
